import { BarList, DonutChart, LineChart, StackedBarChart } from '@platform/design-system';
import type { ReactNode } from 'react';
import { usePageContext } from '../../usePageContext.ts';
import { seriesUnitOf } from '../overview-options.ts';
import { breakdownSeriesOf, breakdownTotalsOf, scaleOfMeasure } from './breakdown-values.ts';
import { tickFormatOf } from './series-ticks.ts';
import { useBreakdownNames } from './use-breakdown-names.ts';
import { useBreakdown, type BreakdownReport } from './widget-queries.ts';
import type { WidgetProps } from './widget-props.ts';
import type { WidgetSettings } from './widget-types.ts';
import { WidgetBody } from './WidgetBody.tsx';

type BreakdownChart = WidgetSettings<'breakdown'>['chart'];

type ChartInput = {
  readonly label: string;
  readonly report: BreakdownReport | undefined;
  readonly loading: boolean;
  readonly emptyTitle: string;
  readonly series: ReturnType<typeof breakdownSeriesOf>;
  readonly totals: ReturnType<typeof breakdownTotalsOf>;
  readonly formatTick: (value: number) => string;
};

const CHARTS: Readonly<Record<BreakdownChart, (input: ChartInput) => ReactNode>> = {
  stacked: (input) => <StackedBarChart {...input} arrangement="stacked" />,
  grouped: (input) => <StackedBarChart {...input} arrangement="grouped" />,
  line: (input) => <LineChart {...input} />,
  barList: (input) => (
    <BarList label={input.label} items={input.totals} emptyTitle={input.emptyTitle} loading={input.loading} />
  ),
  donut: (input) => (
    <DonutChart
      label={input.label}
      slices={input.totals}
      emptyTitle={input.emptyTitle}
      loading={input.loading}
    />
  ),
};

export const BreakdownWidget = ({ widget, context }: WidgetProps<'breakdown'>) => {
  const { t, locale } = usePageContext();
  const { dimension, measure, chart, group } = widget.settings;
  const breakdown = useBreakdown(context, { dimension, measure, unit: seriesUnitOf(group, context.range) });
  const nameOf = useBreakdownNames(context, dimension);
  const report = breakdown.data;
  const input: ChartInput = {
    label: t(`dashboard.widgets.breakdownTitle.${measure}`, {
      dimension: t(`dashboard.widgets.dimension.${dimension}`),
    }),
    report,
    loading: breakdown.isPending,
    emptyTitle: t('dashboard.noOrdersTitle'),
    series: report === undefined ? [] : breakdownSeriesOf(report, nameOf, locale),
    totals:
      report === undefined
        ? []
        : breakdownTotalsOf(report, nameOf, {
            locale,
            versus: (value, previous) => t('dashboard.widgets.versus', { value, previous }),
          }),
    formatTick: tickFormatOf(scaleOfMeasure(measure), locale),
  };
  return <WidgetBody query={breakdown}>{CHARTS[chart](input)}</WidgetBody>;
};
